import { Routes, Route } from 'react-router-dom'
import { ErrorBoundary } from './components/ErrorBoundary'
import { Layout } from './components/layout/Layout'
import { ThemeProvider } from './contexts/ThemeContext'
import { useRestaurantData } from './hooks/useRestaurantData'
import { useMenuData } from './hooks/useMenuData'
import { useOffersData } from './hooks/useOffersData'
import { useRewardsData } from './hooks/useRewardsData'
import { useOrderHistoryData } from './hooks/useOrderHistoryData'
import { useActiveOrderTracking } from './hooks/useActiveOrderTracking'
import { useTokenSync } from './hooks/useTokenSync'
import { useCartSync } from './hooks/useCartSync'
import { useAddressesData } from './hooks/useAddressesData'
import { useFavoritesCount } from './hooks/useFavoritesCount'
import { useRecoveryRedirect } from './hooks/useRecoveryRedirect'
import { ShowcaseNoticeHost } from './showcase/ShowcaseNotice'
// Pages
import ComponentShowcase from './pages/ComponentShowcase'
import SimpleTest from './pages/SimpleTest'
import MenuPage from './pages/MenuPage'
import OffersPage from './pages/OffersPage'
import RewardsPage from './pages/RewardsPage'
import RewardHistoryPage from './pages/RewardHistoryPage'
import CartPage from './pages/CartPage'
import CheckoutPage from './pages/CheckoutPage'
import OrderTrackingPage from './pages/OrderTrackingPage'
import AccountPage from './pages/AccountPage'
import ManageAccountPage from './pages/ManageAccountPage'
import NotificationsPage from './pages/NotificationsPage'
import FavoritesPage from './pages/FavoritesPage'
import HelpPage from './pages/HelpPage'
import OrdersPage from './pages/OrdersPage'
import NotFoundPage from './pages/NotFoundPage'
import AddressesPage from './pages/AddressesPage'
import ReorderPage from './pages/ReorderPage'
import PreviousOrderPage from './pages/PreviousOrderPage'

// Route protection
import { ProtectedRoute } from './components/ProtectedRoute'

// Development tools
import { BrandShowcase } from './components/BrandShowcase'
import { ThemeShowcase } from './components/ThemeShowcase'
import { LayoutShowcase } from './components/LayoutShowcase'
import ImageContainersPage from './pages/ImageContainersPage'
import { ShowcaseDevNav } from './components/showcase-new/ShowcaseDevNav'

function App() {
  // Sync auth token to API client
  useTokenSync()

  // Load restaurant data from Flask API into store
  useRestaurantData()

  // Load menu data from Flask API into store
  useMenuData()

  // Load offers data from Flask API into store
  useOffersData()

  // Load rewards data from Flask API into store
  useRewardsData()

  // Load order history from Flask API into store (for authenticated users)
  useOrderHistoryData()

  // Poll for active order tracking status and driver location updates
  useActiveOrderTracking()

  // Sync cart with backend for cross-device persistence (for authenticated users)
  useCartSync()

  // Load saved delivery addresses for authenticated users
  useAddressesData()

  // Fetch favorites count so QuickButtonsContainer knows whether to show the Favorites button
  useFavoritesCount()

  // Listen for Supabase PASSWORD_RECOVERY so the auth sheet can open in 'set-new-password' mode
  // when a user lands here via the password-reset email link.
  useRecoveryRedirect()

  return (
    <ErrorBoundary>
        <ShowcaseNoticeHost />
        <ThemeProvider>
          <Layout>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<MenuPage />} />
          <Route path="/test" element={<SimpleTest />} />
          <Route path="/menu" element={<MenuPage />} />
          {/* Per-item deep link — used by Mail Studio's {{item_url}} CTA token.
              itemId is the client MenuItem.id (DB menuitems.menu_item_id uuid).
              MenuPage reads it via useParams and opens the product sheet once
              the menu has loaded. */}
          <Route path="/menu/item/:itemId" element={<MenuPage />} />
          {/* Password-reset landing page from Supabase email link. Renders the
              menu; useRecoveryRedirect() opens the auth sheet in
              'set-new-password' mode once the PASSWORD_RECOVERY event fires. */}
          <Route path="/reset-password" element={<MenuPage />} />
          <Route path="/offers" element={<OffersPage />} />
          <Route path="/rewards" element={<RewardsPage />} />
          <Route path="/rewards/history" element={
            <ProtectedRoute>
              <RewardHistoryPage />
            </ProtectedRoute>
          } />

          {/* Protected routes */}
          <Route path="/cart" element={
            <ProtectedRoute>
              <CartPage />
            </ProtectedRoute>
          } />
          <Route path="/checkout" element={
            <ProtectedRoute>
              <CheckoutPage />
            </ProtectedRoute>
          } />
          <Route path="/reorder" element={
            <ProtectedRoute>
              <ReorderPage />
            </ProtectedRoute>
          } />
          <Route path="/reorder/:orderId" element={
            <ProtectedRoute>
              <ReorderPage />
            </ProtectedRoute>
          } />
          <Route path="/orders/:orderId/details" element={
            <ProtectedRoute>
              <PreviousOrderPage />
            </ProtectedRoute>
          } />
          <Route path="/orders/:orderId" element={
            <ProtectedRoute>
              <OrderTrackingPage />
            </ProtectedRoute>
          } />
          <Route path="/orders" element={
            <ProtectedRoute>
              <OrdersPage />
            </ProtectedRoute>
          } />
          <Route path="/account" element={
            <ProtectedRoute>
              <AccountPage />
            </ProtectedRoute>
          } />
          <Route path="/account/manage" element={
            <ProtectedRoute>
              <ManageAccountPage />
            </ProtectedRoute>
          } />
          <Route path="/account/notifications" element={
            <ProtectedRoute>
              <NotificationsPage />
            </ProtectedRoute>
          } />
          <Route path="/account/addresses" element={
            <ProtectedRoute>
              <AddressesPage />
            </ProtectedRoute>
          } />
          <Route path="/favorites" element={
            <ProtectedRoute>
              <FavoritesPage />
            </ProtectedRoute>
          } />
          <Route path="/help" element={
            <ProtectedRoute>
              <HelpPage />
            </ProtectedRoute>
          } />

          {/* Development tools (only in dev mode) */}
          {import.meta.env.DEV && (
            <>
              <Route path="/dev/*" element={<ComponentShowcase />} />
              <Route path="/brand-showcase" element={<BrandShowcase selectedRestaurant="woodfire" />} />
              <Route path="/theme-showcase" element={<ThemeShowcase />} />
              <Route path="/layout-showcase" element={<LayoutShowcase />} />
              <Route path="/image-containers" element={<ImageContainersPage />} />
            </>
          )}

          {/* 404 route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>

        {/* Dev-only keyboard navigation (Escape to return to showcase) */}
        {import.meta.env.DEV && <ShowcaseDevNav />}
          </Layout>
        </ThemeProvider>
    </ErrorBoundary>
  )
}

export default App